import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, ArrowLeft, Save, ChevronUp, ChevronDown, RotateCcw, ClipboardList } from "lucide-react";

const MATTER_TYPES = ["purchase", "sale", "remortgage", "transfer"];
const TENURES = ["Freehold", "Leasehold"];
const FIELD_TYPES = ["text", "textarea", "email", "date", "number", "currency",
                     "select", "radio", "checkbox", "multiselect", "address"];

/**
 * Firm-admin questionnaire editor. Backed by
 * `/api/firm/questionnaires` — solicitors with `is_admin` can create
 * brand-new forms or override any built-in. Overrides win on the
 * merge, DELETE reverts to the built-in (or removes a custom form).
 */
export default function FirmQuestionnairesPage() {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/firm/questionnaires");
      setList(r.data.questionnaires || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not load"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openEditor = (item) => {
    setSelectedKey(item.key);
    setDraft(JSON.parse(JSON.stringify({
      key: item.key, label: item.label, description: item.description || "",
      applicable_to: item.applicable_to || [], tenures: item.tenures || [],
      sections: item.sections || [{ title: "Section 1", fields: [] }],
      _source: item.source, _is_builtin: item.is_builtin,
    })));
  };

  const openNew = () => {
    setSelectedKey("__new__");
    setDraft({
      key: "", label: "New questionnaire", description: "",
      applicable_to: [], tenures: [],
      sections: [{ title: "Section 1", fields: [] }],
      _source: "custom", _is_builtin: false,
    });
  };

  const save = async () => {
    if (!draft.key || !/^[a-z][a-z0-9_]*$/.test(draft.key)) {
      toast.error("Key must be lowercase letters, digits or underscores (starting with a letter).");
      return;
    }
    setSaving(true);
    try {
      const body = { ...draft }; delete body._source; delete body._is_builtin;
      await api.put(`/firm/questionnaires/${draft.key}`, body);
      toast.success("Saved");
      await load();
      setSelectedKey(draft.key);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const revert = async () => {
    if (!draft._is_builtin && draft._source !== "override") { return; }
    if (!window.confirm("Revert this questionnaire to the built-in default? Your override will be deleted.")) return;
    try {
      await api.delete(`/firm/questionnaires/${draft.key}`);
      toast.success("Reverted to built-in");
      setSelectedKey(null); setDraft(null);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Revert failed"); }
  };

  const remove = async () => {
    if (draft._is_builtin) { return; }
    if (!window.confirm(`Delete "${draft.label}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/firm/questionnaires/${draft.key}`);
      toast.success("Deleted");
      setSelectedKey(null); setDraft(null);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  // ── section + field mutators ──────────────────────────────────────
  const patchDraft = (fn) => setDraft(d => { const c = JSON.parse(JSON.stringify(d)); fn(c); return c; });
  const addSection = () => patchDraft(d => d.sections.push({ title: `Section ${d.sections.length + 1}`, fields: [] }));
  const removeSection = (si) => patchDraft(d => d.sections.splice(si, 1));
  const moveSection = (si, dir) => patchDraft(d => {
    const ni = si + dir; if (ni < 0 || ni >= d.sections.length) return;
    [d.sections[si], d.sections[ni]] = [d.sections[ni], d.sections[si]];
  });
  const addField = (si) => patchDraft(d => d.sections[si].fields.push({
    key: `field_${Date.now().toString(36)}`, label: "New field", type: "text", required: false,
  }));
  const removeField = (si, fi) => patchDraft(d => d.sections[si].fields.splice(fi, 1));
  const moveField = (si, fi, dir) => patchDraft(d => {
    const arr = d.sections[si].fields; const ni = fi + dir;
    if (ni < 0 || ni >= arr.length) return;
    [arr[fi], arr[ni]] = [arr[ni], arr[fi]];
  });

  return (
    <div className="min-h-screen bg-slate-50" data-testid="firm-questionnaires-page">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <button onClick={() => navigate(-1)} className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1" data-testid="back-btn">
            <ArrowLeft className="w-3.5 h-3.5" /> Back
          </button>
          <h1 className="text-base font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Questionnaire editor</h1>
          <div className="w-14" />
        </div>
      </header>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left: list */}
        <aside className="lg:col-span-4">
          <div className="bg-white border border-slate-200 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500">Forms</h3>
              <button onClick={openNew} data-testid="new-questionnaire-btn"
                className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded bg-[#100f5d] text-white hover:opacity-90">
                <Plus className="w-3 h-3" /> New
              </button>
            </div>
            {loading ? <p className="text-xs text-slate-400 py-4 text-center">Loading…</p> : (
              <ul className="space-y-1" data-testid="questionnaires-list">
                {list.map(item => (
                  <li key={item.key}>
                    <button type="button" onClick={() => openEditor(item)} data-testid={`q-list-${item.key}`}
                      className={`w-full text-left px-3 py-2 rounded text-sm border transition-colors ${
                        selectedKey === item.key ? "border-[#100f5d] bg-[#100f5d]/5" : "border-transparent hover:bg-slate-50"
                      }`}>
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-slate-900 truncate">{item.label}</span>
                        <SourceBadge source={item.source} />
                      </div>
                      <p className="text-[11px] text-slate-500 font-mono">{item.key}</p>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </aside>

        {/* Right: editor */}
        <main className="lg:col-span-8">
          {!draft ? (
            <div className="bg-white border border-slate-200 rounded-lg p-10 text-center">
              <ClipboardList className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">Pick a form on the left, or click <strong>New</strong> to create one.</p>
            </div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-6" data-testid="editor-panel">
              {/* Header */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Text label="Key (lowercase, no spaces)" value={draft.key} onChange={v => patchDraft(d => d.key = v.trim())} disabled={draft._is_builtin} testid="edit-key" />
                <Text label="Label (client-facing name)" value={draft.label} onChange={v => patchDraft(d => d.label = v)} testid="edit-label" />
              </div>
              <Text label="Description (client-facing subtitle)" value={draft.description} onChange={v => patchDraft(d => d.description = v)} testid="edit-description" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <MultiCheck label="Applies to matter type" options={MATTER_TYPES} values={draft.applicable_to} onChange={vs => patchDraft(d => d.applicable_to = vs)} testid="edit-applicable" />
                <MultiCheck label="Applies to tenure" options={TENURES} values={draft.tenures} onChange={vs => patchDraft(d => d.tenures = vs)} testid="edit-tenures" />
              </div>

              {/* Sections */}
              <div className="border-t border-slate-100 pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-slate-900">Sections</h4>
                  <button onClick={addSection} data-testid="add-section-btn"
                    className="text-xs font-semibold px-2 py-1 rounded border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-1">
                    <Plus className="w-3 h-3" /> Section
                  </button>
                </div>
                <div className="space-y-3">
                  {draft.sections.map((sec, si) => (
                    <div key={si} className="border border-slate-200 rounded-lg p-3 bg-slate-50/50">
                      <div className="flex items-center gap-2 mb-2">
                        <input type="text" value={sec.title} onChange={e => patchDraft(d => d.sections[si].title = e.target.value)}
                          className="flex-1 text-sm font-semibold bg-transparent border-b border-slate-200 focus:outline-none focus:border-[#100f5d] py-1"
                          data-testid={`section-title-${si}`} />
                        <IconBtn onClick={() => moveSection(si, -1)} testid={`section-up-${si}`}><ChevronUp className="w-3 h-3" /></IconBtn>
                        <IconBtn onClick={() => moveSection(si, 1)} testid={`section-down-${si}`}><ChevronDown className="w-3 h-3" /></IconBtn>
                        <IconBtn onClick={() => removeSection(si)} testid={`section-del-${si}`} danger><Trash2 className="w-3 h-3" /></IconBtn>
                      </div>
                      <div className="space-y-2">
                        {sec.fields.map((f, fi) => (
                          <FieldRow key={fi} field={f} onChange={patch => patchDraft(d => Object.assign(d.sections[si].fields[fi], patch))}
                            onMoveUp={() => moveField(si, fi, -1)} onMoveDown={() => moveField(si, fi, 1)}
                            onDelete={() => removeField(si, fi)} testidBase={`s${si}-f${fi}`} />
                        ))}
                        <button onClick={() => addField(si)} data-testid={`add-field-${si}`}
                          className="w-full text-xs text-slate-500 border border-dashed border-slate-300 rounded py-1.5 hover:bg-white hover:border-slate-400">
                          <Plus className="w-3 h-3 inline mr-1" /> Field
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Action bar */}
              <div className="border-t border-slate-100 pt-4 flex flex-wrap items-center justify-between gap-2">
                <div className="flex gap-2">
                  {draft._source === "override" && (
                    <button onClick={revert} data-testid="revert-btn"
                      className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-2 rounded border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100">
                      <RotateCcw className="w-3 h-3" /> Revert to built-in
                    </button>
                  )}
                  {!draft._is_builtin && selectedKey !== "__new__" && (
                    <button onClick={remove} data-testid="delete-btn"
                      className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-2 rounded border border-red-300 bg-red-50 text-red-700 hover:bg-red-100">
                      <Trash2 className="w-3 h-3" /> Delete
                    </button>
                  )}
                </div>
                <button onClick={save} disabled={saving} data-testid="save-btn"
                  className="inline-flex items-center gap-1 text-sm font-semibold px-5 py-2 rounded bg-[#100f5d] text-white hover:opacity-90 disabled:opacity-50">
                  <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

// ─── little helpers ──────────────────────────────────────────────────
function FieldRow({ field, onChange, onMoveUp, onMoveDown, onDelete, testidBase }) {
  const hasOpts = ["select", "radio", "multiselect"].includes(field.type);
  return (
    <div className="bg-white border border-slate-200 rounded p-2 space-y-2">
      <div className="grid grid-cols-12 gap-2 items-center">
        <input value={field.key} onChange={e => onChange({ key: e.target.value.trim() })}
          placeholder="field_key" className="col-span-3 text-[11px] font-mono border border-slate-200 rounded px-1.5 py-1" data-testid={`${testidBase}-key`} />
        <input value={field.label} onChange={e => onChange({ label: e.target.value })}
          placeholder="Client-facing label" className="col-span-5 text-xs border border-slate-200 rounded px-1.5 py-1" data-testid={`${testidBase}-label`} />
        <select value={field.type} onChange={e => onChange({ type: e.target.value, options: ["select", "radio", "multiselect"].includes(e.target.value) ? (field.options || []) : undefined })}
          className="col-span-2 text-xs border border-slate-200 rounded px-1 py-1" data-testid={`${testidBase}-type`}>
          {FIELD_TYPES.map(t => <option key={t}>{t}</option>)}
        </select>
        <label className="col-span-1 text-[10px] text-slate-600 flex items-center gap-1">
          <input type="checkbox" checked={!!field.required} onChange={e => onChange({ required: e.target.checked })} data-testid={`${testidBase}-req`} /> req
        </label>
        <div className="col-span-1 flex justify-end gap-0.5">
          <IconBtn onClick={onMoveUp} testid={`${testidBase}-up`}><ChevronUp className="w-3 h-3" /></IconBtn>
          <IconBtn onClick={onMoveDown} testid={`${testidBase}-down`}><ChevronDown className="w-3 h-3" /></IconBtn>
          <IconBtn onClick={onDelete} testid={`${testidBase}-del`} danger><Trash2 className="w-3 h-3" /></IconBtn>
        </div>
      </div>
      {hasOpts && (
        <input value={(field.options || []).join(", ")} onChange={e => onChange({ options: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })}
          placeholder="Options (comma-separated)" className="w-full text-[11px] border border-slate-200 rounded px-1.5 py-1" data-testid={`${testidBase}-options`} />
      )}
    </div>
  );
}

function Text({ label, value, onChange, disabled, testid }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-slate-600 mb-1 block">{label}</span>
      <input type="text" value={value} disabled={disabled} onChange={e => onChange(e.target.value)}
        className="w-full text-sm border border-slate-200 rounded px-2 py-1.5 disabled:bg-slate-50 disabled:text-slate-400"
        data-testid={testid} />
    </label>
  );
}
function MultiCheck({ label, options, values, onChange, testid }) {
  const toggle = (o) => onChange(values.includes(o) ? values.filter(x => x !== o) : [...values, o]);
  return (
    <div>
      <span className="text-xs font-medium text-slate-600 mb-1 block">{label}</span>
      <div className="flex flex-wrap gap-1" data-testid={testid}>
        {options.map(o => (
          <button key={o} type="button" onClick={() => toggle(o)}
            className={`text-[11px] px-2 py-1 rounded-full border ${values.includes(o) ? "bg-[#100f5d] text-white border-[#100f5d]" : "bg-white text-slate-600 border-slate-200"}`}
            data-testid={`${testid}-${o}`}>{o}</button>
        ))}
      </div>
    </div>
  );
}
function IconBtn({ children, onClick, danger, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`p-1 rounded border transition-colors ${danger ? "border-red-200 text-red-600 hover:bg-red-50" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}>
      {children}
    </button>
  );
}
function SourceBadge({ source }) {
  const m = { builtin: ["bg-slate-100 text-slate-600", "Built-in"],
              override: ["bg-amber-100 text-amber-700", "Overridden"],
              custom: ["bg-emerald-100 text-emerald-700", "Custom"] }[source] || ["bg-slate-100 text-slate-500", source];
  return <span className={`text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded ${m[0]}`}>{m[1]}</span>;
}
